import { getReportMetricKind } from '../shared/report-table-styles';
import ReportLead from '../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, ThemeProvider, useTheme } from '@mui/material/styles';
import { CancelRounded, CheckCircleRounded, HelpOutlineRounded } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { AppVarsContext } from '../../context/AppVarsContext';
import { formatUsd } from '../../system/report-formatters';
import { getReportTabsSx } from '../shared/reportSurfaceStyles';
import { createReportSurfaceTheme, getReportSurfaceSx, reportColors, styleReportChart } from '../shared/report-design';

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatPercent = (value, digits = 1) => `${Number(value || 0).toFixed(digits)}%`;
const formatDonationTypeLabel = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'recurring') return 'Recurring';
    if (normalized === 'one-time') return 'One-Time';
    return 'Unknown';
};

const ANONYMOUS_LABEL = 'Anonymous';

const isReadableDonationLabel = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return false;
    const normalized = text.replace(/[$,\s]/g, '');
    if (/^\d+(\.\d+)?$/.test(normalized)) return false;
    return true;
};

const resolveDonationLabel = (row, preferredKeys = []) => {
    if (row == null) return '—';
    if (typeof row === 'string' || typeof row === 'number') return String(row);
    if (typeof row !== 'object') return '—';

    const primaryCandidates = [
        ...preferredKeys,
        'label',
        'name',
        'title',
        'campaignName',
        'campaign_name',
        'campaignLabel',
        'campaign_label',
        'campaignOriginal',
        'trackerLabel',
        'tracker_label',
        'designationName',
        'designation_name',
        'designationLabel',
        'designation_label',
        'fundName',
        'fund_name',
        'projectName',
        'project_name',
        'appealName',
        'appeal_name',
        'programName',
        'program_name'
    ];

    for (const key of primaryCandidates) {
        const value = row?.[key];
        if (value === null || value === undefined) continue;
        const text = String(value).trim();
        if (isReadableDonationLabel(text)) return text;
    }

    for (const key of primaryCandidates) {
        const value = row?.[key];
        if (value === null || value === undefined) continue;
        const text = String(value).trim();
        if (text) return text;
    }

    return '—';
};

const normalizeDonationBreakdownRows = (rows, preferredKeys) => (
    Array.isArray(rows)
        ? rows.map((row) => ({
            ...row,
            label: resolveDonationLabel(row, preferredKeys)
        }))
        : []
);

const anonymizeTransactionRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    return {
        ...row,
        donorName: ANONYMOUS_LABEL,
        donorEmail: ANONYMOUS_LABEL
    };
};

const anonymizeDonationReport = (payload) => {
    if (!payload || typeof payload !== 'object') return payload;
    return {
        ...payload,
        donations: Array.isArray(payload.donations)
            ? payload.donations.map(anonymizeTransactionRow)
            : payload.donations
    };
};

const EmptyState = ({ message }) => (
    <Box py={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
);

const SummaryMetricCard = ({ label, value, helper }) => (
    <Card variant="outlined" sx={getReportSurfaceSx(useTheme())}>
        <CardContent>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.75 }}>
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {helper}
            </Typography>
        </CardContent>
    </Card>
);

const TabPanel = ({ value, index, children }) => {
    if (value !== index) return null;
    return <Box sx={{ minWidth: 0 }} role="tabpanel" id={`donations-panel-${index}`} aria-labelledby={`donations-tab-${index}`}>{children}</Box>;
};

export const SimpleTable = ({ rows, columns, emptyMessage, getRowSx }) => {
    const theme = useTheme();
    const useResponsiveRows = useMediaQuery(theme.breakpoints.down('md'));

    if (!rows.length) {
        return <EmptyState message={emptyMessage} />;
    }

    if (useResponsiveRows) {
        return (
            <Box role="list" sx={{ display: 'grid' }}>
                {rows.map((row, index) => (
                    <Box
                        component="article"
                        role="listitem"
                        key={`${row.id || row.date || 'row'}-${index}`}
                        sx={{
                            py: 1.5,
                            px: { xs: 0.25, sm: 0.75 },
                            borderBottom: index === rows.length - 1 ? 0 : `1px solid ${theme.palette.divider}`,
                            ...getRowSx?.(row),
                        }}
                    >
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                            {columns.map((column, columnIndex) => (
                                <Box data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} sx={{ minWidth: 0, gridColumn: columnIndex === 0 ? { xs: '1', sm: '1 / -1' } : 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.35 }}>
                                        {column.label}
                                    </Typography>
                                    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere', textAlign: 'left' }}>
                                        {column.render ? column.render(row[column.key], row) : row[column.key]}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box role="region" aria-label="Scrollable donation table" tabIndex={0} sx={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0, WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={{ minWidth: 680, '& th, & td': { px: 2, py: 1.25 }, '& td': { overflowWrap: 'anywhere', maxWidth: 320 } }}>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} align={column.align || 'left'}>
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.id || row.date || 'row'}-${index}`} sx={getRowSx ? getRowSx(row) : undefined}>
                            {columns.map((column) => (
                                <TableCell data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} align={column.align || 'left'}>
                                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default function DonationPlatformReport({ title = 'Donation Platform', setLoading: setExternalLoading }) {
    const rid = sessionStorage.getItem('rid') || sessionStorage.getItem('cid') || '';
    const { appVars, appLoading } = useContext(AppVarsContext);
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [providerStatus, setProviderStatus] = useState({ status: '', provider: '', providers: [] });
    const [selectedProvider, setSelectedProvider] = useState('');
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [error, setError] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedDateS, setSelectedDateS] = useState(sessionStorage.getItem('sDate') || '');
    const [selectedDateE, setSelectedDateE] = useState(sessionStorage.getItem('eDate') || '');
    const requestIdRef = useRef(0);

    useEffect(() => {
        const handleStorageChange = (event) => {
            setSelectedDateS(event?.detail?.sDate || sessionStorage.getItem('sDate') || '');
            setSelectedDateE(event?.detail?.eDate || sessionStorage.getItem('eDate') || '');
        };

        window.addEventListener('dc', handleStorageChange);
        return () => {
            window.removeEventListener('dc', handleStorageChange);
        };
    }, []);

    const fetchStatus = useCallback(async () => {
        if (!appVars?.api_url || !rid) return;
        setStatusLoading(true);
        try {
            const response = await fetch(`${appVars.api_url}/donation-platform/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cid: rid })
            });
            const payload = await response.json();
            setProviderStatus(payload || { status: '' });
            setSelectedProvider((current) => current || payload?.provider || payload?.providers?.[0]?.id || '');
        } catch (loadError) {
            console.error('DonationPlatformReport status load failed', loadError);
            setProviderStatus({ status: 'invalid', providers: [] });
        } finally {
            setStatusLoading(false);
        }
    }, [appVars?.api_url, rid]);

    const fetchReport = useCallback(async () => {
        if (!appVars?.api_url || !rid || !selectedDateS || !selectedDateE || !selectedProvider) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${appVars.api_url}/donation-platform/report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: rid,
                    sdate: selectedDateS,
                    edate: selectedDateE,
                    provider: selectedProvider
                })
            });
            const payload = await response.json();
            if (!response.ok || payload?.ok === false) {
                throw new Error(payload?.detail || payload?.error || `Donation platform report failed (${response.status})`);
            }
            if (requestId !== requestIdRef.current) return;
            setReport(anonymizeDonationReport(payload));
        } catch (loadError) {
            if (requestId !== requestIdRef.current) return;
            console.error('DonationPlatformReport fetch failed', loadError);
            setReport(null);
            setError(loadError?.message || 'Failed to load donation platform report.');
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, [appVars?.api_url, rid, selectedDateE, selectedDateS, selectedProvider]);

    useEffect(() => {
        if (!appLoading && rid) {
            fetchStatus();
        }
    }, [appLoading, rid, fetchStatus]);

    useEffect(() => {
        if (!appLoading && selectedProvider && selectedDateS && selectedDateE) {
            fetchReport();
        }
    }, [appLoading, fetchReport, selectedDateE, selectedDateS, selectedProvider]);

    useEffect(() => {
        if (typeof setExternalLoading === 'function') {
            setExternalLoading(Boolean(appLoading || statusLoading || loading));
        }
        return () => {
            if (typeof setExternalLoading === 'function') {
                setExternalLoading(false);
            }
        };
    }, [appLoading, loading, setExternalLoading, statusLoading]);

    const currencyCode = useMemo(() => {
        const currencies = report?.totals?.currencies || [];
        return currencies[0] || 'USD';
    }, [report?.totals?.currencies]);

    const formatCurrency = useCallback((value, currency = currencyCode) => {
        if ((currency || 'USD') === 'USD') {
            return formatUsd(value);
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(value || 0));
    }, [currencyCode]);

    const providerChips = useMemo(() => providerStatus?.providers || [], [providerStatus?.providers]);
    const reportRows = useMemo(() => report?.donations || [], [report?.donations]);
    const reconciliation = report?.reconciliation || null;
    const totalRevenue = report?.totals?.grossAmount || 0;
    const avgDonation = report?.totals?.averageAmount || 0;
    const uniqueDonors = report?.totals?.uniqueDonors || 0;
    const recurringCount = report?.totals?.recurringCount || 0;
    const oneTimeCount = report?.totals?.oneTimeCount || 0;
    const unknownTypeCount = report?.totals?.unknownTypeCount || 0;
    const recurringShare = report?.totals?.donationCount
        ? (Number(report?.totals?.recurringCount || 0) / Number(report?.totals?.donationCount || 1)) * 100
        : 0;

    const trendRows = useMemo(() => report?.charts?.byDate || [], [report?.charts?.byDate]);
    const typeRows = useMemo(() => report?.charts?.byType || [], [report?.charts?.byType]);
    const statusRows = useMemo(() => report?.charts?.byStatus || [], [report?.charts?.byStatus]);
    const methodRows = useMemo(() => report?.charts?.byMethod || [], [report?.charts?.byMethod]);
    const campaignRows = useMemo(() => normalizeDonationBreakdownRows(report?.charts?.topCampaigns, [
        'campaignName',
        'campaign_name',
        'campaign',
        'campaignLabel',
        'campaign_label',
        'campaignOriginal',
        'trackerLabel'
    ]), [report?.charts?.topCampaigns]);
    const designationRows = useMemo(() => normalizeDonationBreakdownRows(report?.charts?.topDesignations, [
        'designationName',
        'designation_name',
        'designation',
        'designationLabel',
        'designation_label',
        'fundName',
        'fund_name',
        'projectName',
        'project_name',
        'appealName',
        'appeal_name',
        'programName',
        'program_name'
    ]), [report?.charts?.topDesignations]);

    const trendOptions = useMemo(() => styleReportChart(theme, {
        chart: { type: 'line' },
        colors: [reportColors.revenue, reportColors.conversions],
        stroke: { width: [0, 3], curve: 'straight' },
        plotOptions: { bar: { columnWidth: '44%', borderRadius: 4 } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: trendRows.map((row) => row.date),
            labels: { rotate: isMobile ? -35 : 0, formatter: (value) => String(value).slice(5).replace('-', '/') }
        },
        yaxis: [
            {
                title: { text: 'Amount' },
                labels: { formatter: (value) => formatCurrency(value) }
            },
            {
                opposite: true,
                max: Math.max(1, ...trendRows.map((row) => Number(row.count || 0))),
                tickAmount: Math.min(5, Math.max(1, ...trendRows.map((row) => Number(row.count || 0)))),
                title: { text: 'Donations' },
                labels: { formatter: (value) => formatNumber(value) }
            }
        ],
        grid: {
            borderColor: alpha(theme.palette.primary.main, 0.12)
        },
        tooltip: {
            shared: true,
            intersect: false,
            y: [
                { formatter: (value) => formatCurrency(value) },
                { formatter: (value) => formatNumber(value) }
            ]
        }
    }), [formatCurrency, isMobile, theme, trendRows]);

    const trendSeries = useMemo(() => ([
        {
            name: 'Revenue',
            type: 'column',
            data: trendRows.map((row) => Number(row.amount || 0))
        },
        {
            name: 'Donations',
            type: 'line',
            data: trendRows.map((row) => Number(row.count || 0))
        }
    ]), [trendRows]);

    const buildHorizontalOptions = useCallback((rows, color, formatter, isCount = true) => styleReportChart(theme, {
        chart: { type: 'bar' },
        colors: [color],
        tooltip: { y: { formatter } },
        legend: { show: false },
        plotOptions: { bar: { horizontal: true, barHeight: '45%', borderRadius: 6, distributed: false } },
        dataLabels: {
            enabled: false,
            formatter
        },
        xaxis: {
            categories: rows.map((row) => row.label),
            tickAmount: isCount ? Math.min(5, Math.max(1, ...rows.map((row) => Number(row.count || 0)))) : 5,
            ...(isCount ? { min: 0, max: Math.max(1, ...rows.map((row) => Number(row.count || 0))) } : {}),
            labels: { formatter },
        },
        grid: {
            borderColor: alpha(theme.palette.primary.main, 0.12)
        }
    }), [theme]);

    const transactionColumns = useMemo(() => ([
        {
            key: 'trackingMatch',
            label: 'Internal Tracking',
            render: (match) => {
                const status = match?.status || 'unknown';
                const isTracked = status === 'tracked';
                const isUnmatched = status === 'unmatched';
                const label = isTracked
                    ? (match?.matchedBy === 'transaction_id' ? 'Tracked · Exact' : 'Tracked · Probable')
                    : (isUnmatched ? 'Not tracked' : 'Not compared');
                const detail = isTracked
                    ? `${(match?.reasons || []).join(' + ')}${match?.internal?.date ? ` · Internal ${new Date(match.internal.date).toLocaleString()}` : ''}`
                    : (isUnmatched ? 'No reliable internal transaction match was found.' : 'Internal comparison is unavailable.');
                const Icon = isTracked ? CheckCircleRounded : (isUnmatched ? CancelRounded : HelpOutlineRounded);

                return (
                    <Stack spacing={0.35} sx={{ minWidth: 150 }}>
                        <Tooltip title={detail} arrow>
                            <Chip
                                size="small"
                                icon={<Icon />}
                                label={label}
                                color={isTracked ? 'success' : (isUnmatched ? 'error' : 'default')}
                                variant={isTracked || isUnmatched ? 'filled' : 'outlined'}
                                sx={{ width: 'fit-content', fontWeight: 800 }}
                            />
                        </Tooltip>
                        {isTracked ? <Typography variant="caption" color="text.secondary">{(match?.reasons || []).join(' + ')}</Typography> : null}
                    </Stack>
                );
            }
        },
        {
            key: 'date',
            label: 'Date',
            render: (_, row) => (
                <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.date ? new Date(row.date).toLocaleDateString() : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.date ? new Date(row.date).toLocaleTimeString() : ''}
                    </Typography>
                </Stack>
            )
        },
        {
            key: 'donorName',
            label: 'Donor',
            render: (_, row) => (
                <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.donorName || ANONYMOUS_LABEL}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.donorEmail || ANONYMOUS_LABEL}
                    </Typography>
                </Stack>
            )
        },
        {
            key: 'type',
            label: 'Type',
            render: (value) => <Chip size="small" label={formatDonationTypeLabel(value)} variant="outlined" />
        },
        {
            key: 'amount',
            label: 'Amount',
            align: 'right',
            render: (value, row) => (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatCurrency(value, row.currency || currencyCode)}
                </Typography>
            )
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => <Chip size="small" label={String(value || 'unknown')} variant="outlined" />
        },
        {
            key: 'campaign',
            label: 'Campaign',
            render: (value, row) => resolveDonationLabel(row, ['campaignName', 'campaign_name', 'campaign', 'campaignLabel', 'campaign_label', 'campaignOriginal', 'trackerLabel']) || value || '—'
        },
        {
            key: 'designation',
            label: 'Designation',
            render: (value, row) => resolveDonationLabel(row, ['designationName', 'designation_name', 'designation', 'designationLabel', 'designation_label', 'fundName', 'fund_name', 'projectName', 'project_name', 'appealName', 'appeal_name', 'programName', 'program_name']) || value || '—'
        },
        {
            key: 'method',
            label: 'Method',
            render: (value) => value || '—'
        }
    ]), [currencyCode, formatCurrency]);

    const transactionRowSx = useCallback((row) => {
        const status = row?.trackingMatch?.status;
        if (status === 'tracked') {
            return { bgcolor: alpha(theme.palette.success.main, 0.075), '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.12) } };
        }
        if (status === 'unmatched') {
            return { bgcolor: alpha(theme.palette.error.main, 0.065), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.11) } };
        }
        return undefined;
    }, [theme]);

    const reportSubtitle = providerStatus?.providerLabel
        ? `${providerStatus.providerLabel} connected`
        : 'Donation platform report';

    return (
        <ThemeProvider theme={theme}><Box sx={{ minWidth: 0, bgcolor: 'background.default',
            '& .MuiCardHeader-title': { fontSize: '1rem', fontWeight: 700 },
            '& .MuiCardHeader-subheader': { fontSize: '0.875rem', mt: 0.5 },
            '& .MuiTableCell-head': { bgcolor: 'background.default', fontWeight: 700, whiteSpace: 'nowrap' } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2, p: { xs: 1, sm: 2, lg: 3 }, minWidth: 0, '@media print': { p: 0 } }}>
                        <Box sx={{ ...getReportSurfaceSx(theme), p: 1 }}>
                            <Tabs aria-label="Donation report sections" value={tabIndex} onChange={(_, nextValue) => setTabIndex(nextValue)} sx={getReportTabsSx(theme)}>
                                <Tab label="Overview" id="donations-tab-0" aria-controls="donations-panel-0" />
                                <Tab label="Transactions" id="donations-tab-1" aria-controls="donations-panel-1" />
                            </Tabs>
                        </Box>
                <Box sx={{ ...getReportSurfaceSx(theme), p: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        <ReportLead eyebrow="Donations" title={title === 'Donation Platform' ? 'Donation Performance' : title} description="See how giving, donor participation, and recurring support drive fundraising results." />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                            <Chip
                                label={report?.providerLabel || providerStatus?.providerLabel || 'No platform connected'}
                                variant="outlined"
                            />
                            <Chip
                                label={reportSubtitle}
                                variant="outlined"
                            />
                        </Stack>
                    </Box>

                    {providerChips.length > 1 ? (
                        <ToggleButtonGroup
                            aria-label="Donation platform"
                            exclusive
                            value={selectedProvider}
                            onChange={(_, nextValue) => {
                                if (nextValue) setSelectedProvider(nextValue);
                            }}
                            size="small"
                            sx={{
                                alignSelf: 'flex-start',
                                '& .MuiToggleButton-root': {
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    px: 1.5,
                                    py: 0.75,
                                    minHeight: 44,
                                },
                            }}
                        >
                            {providerChips.map((provider) => (
                                <ToggleButton key={provider.id} value={provider.id}>
                                    {provider.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ) : null}
                </Box>

                {statusLoading ? <LinearProgress color="primary" /> : null}
                {loading ? <LinearProgress color="primary" /> : null}

                {error ? (
                    <Alert severity="error" sx={{ borderRadius: 3 }}>
                        {error}
                    </Alert>
                ) : null}

                {!statusLoading && providerStatus?.status === 'invalid' ? (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                        No supported donation platform is connected for this client yet. Connect Virtuous, Qgiv, Fundraise Up, GoFundMe Pro, Salsa Labs, NXT, or Bloomerang in integrations first.
                    </Alert>
                ) : null}

                {report?.truncated ? (
                    <Alert severity="warning" sx={{ borderRadius: 3 }}>
                        This provider returned more data than the first pass loaded. The report is usable, but long date ranges may need pagination tuning for complete history.
                    </Alert>
                ) : null}

                {report ? (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' },
                                gap: 1.5,
                            }}
                        >
                            <SummaryMetricCard
                                label="Donations"
                                value={formatNumber(report?.totals?.donationCount)}
                                helper="Total transactions in range"
                            />
                            <SummaryMetricCard
                                label="Revenue"
                                value={formatCurrency(totalRevenue)}
                                helper={`${report?.totals?.currencies?.join(', ') || currencyCode}${(report?.totals?.currencies || []).length > 1 ? ' mixed currencies' : ''}`}
                            />
                            <SummaryMetricCard
                                label="Average Gift"
                                value={formatCurrency(avgDonation)}
                                helper="Average amount per donation"
                            />
                            <SummaryMetricCard
                                label="Unique Donors"
                                value={formatNumber(uniqueDonors)}
                                helper={`${formatPercent(recurringShare)} recurring share`}
                            />
                            <SummaryMetricCard
                                label="Recurring"
                                value={formatNumber(recurringCount)}
                                helper="Recurring donations in range"
                            />
                            <SummaryMetricCard
                                label="One-Time"
                                value={formatNumber(oneTimeCount)}
                                helper={unknownTypeCount ? `${formatNumber(unknownTypeCount)} unknown cadence` : 'One-time donations in range'}
                            />
                        </Box>

                        <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                            <CardHeader
                                title="Internal Tracking Reconciliation"
                                subheader="Donation-platform transactions matched to ReportWerks conversion records by transaction ID, then amount, date, and donor evidence."
                            />
                            <CardContent>
                                {reconciliation?.available === false ? (
                                    <Alert severity="warning">{reconciliation?.error || 'Internal tracking comparison is temporarily unavailable.'}</Alert>
                                ) : reconciliation ? (
                                    <Stack spacing={1.5}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
                                            <SummaryMetricCard label="Coverage" value={formatPercent(reconciliation.coveragePercent)} helper="Platform donations tracked internally" />
                                            <SummaryMetricCard label="Tracked" value={formatNumber(reconciliation.tracked)} helper={`${formatNumber(reconciliation.exact)} exact · ${formatNumber(reconciliation.probable)} probable`} />
                                            <SummaryMetricCard label="Not Tracked" value={formatNumber(reconciliation.unmatched)} helper="Needs tracking investigation" />
                                            <SummaryMetricCard label="Compared" value={formatNumber(reconciliation.total)} helper={`${formatNumber(reconciliation.internalCandidates)} internal candidates`} />
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.max(0, Math.min(100, Number(reconciliation.coveragePercent || 0)))}
                                            color={Number(reconciliation.unmatched || 0) > 0 ? 'warning' : 'success'}
                                            aria-label={`Internal tracking coverage ${formatPercent(reconciliation.coveragePercent)}`}
                                            sx={{ height: 10, borderRadius: 999 }}
                                        />
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            <Chip icon={<CheckCircleRounded />} color="success" label="Green: tracked internally" />
                                            <Chip icon={<CancelRounded />} color="error" label="Red: no reliable match" />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Waiting for reconciliation data from the reporting API.</Typography>
                                )}
                            </CardContent>
                        </Card>



                        <TabPanel value={tabIndex} index={0}>
                            <Stack spacing={2}>
                                <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                    <CardHeader
                                        title="Revenue Trend"
                                        subheader="Daily donation count and revenue for the selected range."
                                    />
                                    <CardContent>
                                        {trendRows.length ? (
                                            <ReactApexChart options={trendOptions} series={trendSeries} type="line" height={360} />
                                        ) : (
                                            <EmptyState message="No donations returned for the selected date range." />
                                        )}
                                    </CardContent>
                                </Card>

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                                    <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                        <CardHeader title="Donation Type" subheader="Recurring vs one-time donations, normalized from the connected platform where available." />
                                        <CardContent>
                                            {typeRows.length ? (
                                                <ReactApexChart
                                                    options={buildHorizontalOptions(typeRows, reportColors.conversions, (value) => formatNumber(value))}
                                                    series={[{ name: 'Donations', data: typeRows.map((row) => row.count) }]}
                                                    type="bar"
                                                    height={300}
                                                />
                                            ) : (
                                                <EmptyState message="No donation type breakdown available." />
                                            )}
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                        <CardHeader title="Status Mix" subheader="Success, refund, failure, and other platform statuses." />
                                        <CardContent>
                                            {statusRows.length ? (
                                                <ReactApexChart
                                                    options={buildHorizontalOptions(statusRows, reportColors.conversions, (value) => formatNumber(value))}
                                                    series={[{ name: 'Donations', data: statusRows.map((row) => Number(row.count || 0)) }]}
                                                    type="bar"
                                                    height={300}
                                                />
                                            ) : (
                                                <EmptyState message="No status breakdown available." />
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                                    <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                        <CardHeader title="Payment Methods" subheader="Donation mix by method or payment source." />
                                        <CardContent>
                                            {methodRows.length ? (
                                                <ReactApexChart
                                                    options={buildHorizontalOptions(methodRows, reportColors.categories[2], (value) => formatNumber(value))}
                                                    series={[{ name: 'Donations', data: methodRows.map((row) => row.count) }]}
                                                    type="bar"
                                                    height={320}
                                                />
                                            ) : (
                                                <EmptyState message="No payment method breakdown available." />
                                            )}
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                        <CardHeader title="Top Campaigns" subheader="Highest-value campaigns or appeals returned by the platform." />
                                        <CardContent>
                                            {campaignRows.length ? (
                                                <ReactApexChart
                                                    options={buildHorizontalOptions(campaignRows, reportColors.revenue, (value) => formatCurrency(value), false)}
                                                    series={[{ name: 'Revenue', data: campaignRows.map((row) => Number(row.amount || 0)) }]}
                                                    type="bar"
                                                    height={320}
                                                />
                                            ) : (
                                                <EmptyState message="No campaign values available from this platform." />
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                                    <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                        <CardHeader title="Top Campaigns Table" subheader="Count and revenue by campaign or appeal." />
                                        <CardContent>
                                            <SimpleTable
                                                rows={campaignRows}
                                                emptyMessage="No campaign attribution returned by this platform."
                                                columns={[
                                                    { key: 'label', label: 'Campaign' },
                                                    { key: 'count', label: 'Donations', align: 'right', render: (value) => formatNumber(value) },
                                                    { key: 'amount', label: 'Revenue', align: 'right', render: (value) => formatCurrency(value) }
                                                ]}
                                            />
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                        <CardHeader title="Top Designations" subheader="Fund, designation, or project breakdown where available." />
                                        <CardContent>
                                            <SimpleTable
                                                rows={designationRows}
                                                emptyMessage="No designation breakdown returned by this platform."
                                                columns={[
                                                    { key: 'label', label: 'Designation' },
                                                    { key: 'count', label: 'Donations', align: 'right', render: (value) => formatNumber(value) },
                                                    { key: 'amount', label: 'Revenue', align: 'right', render: (value) => formatCurrency(value) }
                                                ]}
                                            />
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Stack>
                        </TabPanel>

                        <TabPanel value={tabIndex} index={1}>
                            <Card variant="outlined" sx={getReportSurfaceSx(theme)}>
                                <CardHeader
                                    title="Transactions"
                                    subheader="Direct platform transactions for the selected date range."
                                />
                                <CardContent>
                                    <SimpleTable
                                        rows={reportRows}
                                        columns={transactionColumns}
                                        getRowSx={transactionRowSx}
                                        emptyMessage="No transactions returned for the selected date range."
                                    />
                                </CardContent>
                            </Card>
                        </TabPanel>
                    </>
                ) : null}
            </Box>
        </Box></ThemeProvider>
    );
}
